import type {DocumentCategory, FenzSafeAdapter, FenzSafeRepresentationStatus} from './fenzsafeStore';
import {fenzsafeStore} from './fenzsafeStore';
import {loadGuestDocumentBlob, pruneGuestDocumentBlobs, saveGuestDocumentBlob} from './fenzsafeGuestDocumentBytes';

const STORAGE_KEY = 'profenz_fenzsafe_guest_claim';
const MAX_AGE_MS = 48 * 60 * 60 * 1000;
export type GuestClaimDocumentShell = {
  name: string; category: DocumentCategory; blobKey: string;
  // Write-ahead stage: never repeat an ambiguous non-idempotent add.
  stage?: 'adding' | 'created'; documentId?: string;
};
export type GuestClaimMatterShell = {
  name: string; reference: string; representationStatus: FenzSafeRepresentationStatus | null;
  documents: GuestClaimDocumentShell[]; idempotencyKey: string;
};
type StoredEnvelope = {savedAt: string; matters: GuestClaimMatterShell[]; targetScope?: string};
export type GuestClaimConsent = {scope: string; confirmed: boolean; isCurrent: () => boolean};
type ClaimAdapter = Pick<FenzSafeAdapter, 'createMatter' | 'getSnapshot' | 'addDocument' | 'registerDocumentBytes'>;

// Serializes the whole conversion, including remote partial saves, across browser tabs.
// Unsupported browsers keep their guest tab intact instead of risking duplicate uploads.
async function exclusively<T>(work: () => Promise<T>): Promise<T> {
  if (!navigator.locks) throw new Error('This browser cannot safely transfer guest work. Keep this tab open and use a supported browser.');
  return navigator.locks.request('profenz-fenzsafe-guest-claim', {mode: 'exclusive', ifAvailable: true}, lock => {
    if (!lock) throw new Error('Guest work is being saved in another tab. Wait for that tab to finish.');
    return work();
  });
}
function blobKeys(envelope: StoredEnvelope | null): string[] {
  return envelope?.matters.flatMap(m => m.documents.map(d => d.blobKey)) ?? [];
}
function readEnvelope(): StoredEnvelope | null {
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  const parsed = JSON.parse(raw) as StoredEnvelope;
  const time = Date.parse(parsed.savedAt);
  if (!Number.isFinite(time) || time > Date.now() || Date.now() - time > MAX_AGE_MS ||
      !Array.isArray(parsed.matters) || parsed.matters.length === 0 ||
      !parsed.matters.every(m => typeof m.name === 'string' && typeof m.reference === 'string' &&
        typeof m.idempotencyKey === 'string' && Array.isArray(m.documents) && m.documents.every(d =>
          typeof d.name === 'string' && typeof d.category === 'string' && typeof d.blobKey === 'string')) ||
      (parsed.targetScope !== undefined && typeof parsed.targetScope !== 'string')) return null;
  return parsed;
}
function persist(envelope: StoredEnvelope): void {
  if (envelope.matters.length) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(envelope));
  else window.localStorage.removeItem(STORAGE_KEY);
}

/** Stage bytes first. Navigation is allowed only after both bytes and metadata are saved. */
export async function saveGuestClaimSnapshot(): Promise<void> {
  await exclusively(async () => {
    const matters: GuestClaimMatterShell[] = [];
    for (const matter of fenzsafeStore.getSnapshot().matters.filter(m => m.name.trim())) {
      const idempotencyKey = crypto.randomUUID();
      const documents: GuestClaimDocumentShell[] = [];
      for (const doc of matter.documents) {
        if (doc.processingStatus !== 'accepted' || doc.supersededBy) continue;
        const blobKey = `${idempotencyKey}:${documents.length}`;
        await saveGuestDocumentBlob(blobKey, await fenzsafeStore.downloadDocumentBytes(doc.id));
        documents.push({name: doc.name, category: doc.category, blobKey});
      }
      matters.push({name: matter.name, reference: matter.reference, representationStatus: matter.representationStatus, idempotencyKey, documents});
    }
    const envelope = {savedAt: new Date().toISOString(), matters};
    persist(envelope); // Throws on quota/blocked storage; never navigate on that failure.
    await pruneGuestDocumentBlobs(blobKeys(envelope));
  });
}

/** Sweep on access/startup, including bytes orphaned by interrupted snapshot writes. */
export async function cleanupGuestClaimStorage(): Promise<void> {
  await exclusively(async () => {
    let envelope: StoredEnvelope | null = null;
    try { envelope = readEnvelope(); } catch { /* Malformed or expired state is not claimable. */ }
    await pruneGuestDocumentBlobs(blobKeys(envelope));
    if (!envelope) window.localStorage.removeItem(STORAGE_KEY);
  });
}
export function readGuestClaimSnapshot(): GuestClaimMatterShell[] | null {
  // Reads do not race mutations for the transfer lock. Entry/workspace startup owns cleanup.
  try { return readEnvelope()?.matters ?? null; } catch { /* Invalid state is not claimable. */ }
  return null;
}
export function guestClaimAvailableFor(scope: string): boolean {
  try { const e = readEnvelope(); return !!e && (!e.targetScope || e.targetScope === scope); } catch { return false; }
}
export async function clearGuestClaimSnapshot(): Promise<void> {
  await exclusively(async () => {
    await pruneGuestDocumentBlobs([]);
    window.localStorage.removeItem(STORAGE_KEY);
  });
}

/** No automatic import. Explicit consent binds all retries to the chosen account+organization. */
export async function claimPendingGuestMatters(adapter: ClaimAdapter, consent?: GuestClaimConsent): Promise<GuestClaimMatterShell[]> {
  if (!consent?.confirmed || !consent.scope || !consent.isCurrent()) return [];
  return exclusively(async () => {
    const envelope = readEnvelope();
    if (!envelope) return [];
    if (envelope.targetScope && envelope.targetScope !== consent.scope) throw new Error('This guest transfer belongs to a different account.');
    const check = () => { if (!consent.isCurrent()) throw new Error('Your account changed. Stop and sign in to the account that started this transfer.'); };
    check();
    envelope.targetScope = consent.scope;
    persist(envelope); // Must be durable before any authenticated mutation.
    const claimed: GuestClaimMatterShell[] = [];
    while (envelope.matters.length) {
      check();
      const shell = envelope.matters[0];
      const matterId = await adapter.createMatter(shell.name, shell.reference, shell.representationStatus, shell.idempotencyKey);
      check();
      let current = adapter.getSnapshot().matters.find(m => m.id === matterId);
      if (!current) throw new Error('Reload FenzSafe before continuing the guest transfer.');
      while (shell.documents.length) {
        check();
        const doc = shell.documents[0];
        const file = await loadGuestDocumentBlob(doc.blobKey);
        check();
        if (!file) throw new Error('A guest document is missing from this browser. The transfer is incomplete; upload the original from your device.');
        if (doc.stage === 'adding') throw new Error('A document save could not be confirmed. Check the saved matter before uploading again; no duplicate was created by this retry.');
        if (!doc.documentId) {
          const before = new Set(current.documents.map(d => d.id));
          doc.stage = 'adding';
          persist(envelope); // A lost response must never result in a second blind add.
          current = await adapter.addDocument(matterId, current.version,
            {name: doc.name, category: doc.category, source: 'upload', sizeKb: Math.max(1, Math.round(file.size / 1024))}, `${doc.blobKey}:add`);
          check();
          const added = current.documents.filter(d => !before.has(d.id) && d.name === doc.name && d.category === doc.category);
          if (added.length !== 1) throw new Error('The new document could not be identified safely. Check the saved matter.');
          doc.documentId = added[0].id;
          doc.stage = 'created';
          persist(envelope); // Save metadata ID before bytes; registration retries use the same ID.
        }
        current = await adapter.registerDocumentBytes(matterId, current.version, doc.documentId, new File([file], doc.name, {type: file.type}), `${doc.blobKey}:bytes`);
        check();
        // Upload receipt can still mean scan pending. Do not call it accepted or ready to share.
        const registered = current.documents.find(d => d.id === doc.documentId);
        if (!registered || registered.processingStatus === 'awaiting_upload' || registered.processingStatus === 'unavailable') {
          throw new Error('Document upload is not confirmed. Reload the saved matter before continuing.');
        }
        shell.documents.shift();
        persist(envelope);
        await pruneGuestDocumentBlobs(blobKeys(envelope));
      }
      claimed.push({...shell, documents: []});
      envelope.matters.shift();
      persist(envelope);
    }
    await pruneGuestDocumentBlobs([]);
    return claimed;
  });
}
